import { useTerminology } from '@/lib/useTerminology'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { sectionTitle, cardTitle, fieldLabel } from '@/lib/form-styles'
import { bestaetige } from '@/lib/dialoge'
import { FeldStatus, FeldStatusZeichen, useAenderung, useStellenDaten, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { useZeitKonfiguration } from '@/lib/zeit-konfiguration'
import type { CustomFieldSchema } from '../types/time-tracking.types'
import { X } from 'lucide-react'

/**
 * Verwaltet die Zusatzfelder eines Typs vollständig an einer Stelle:
 * Felddefinition (anlegen/löschen) UND Auswahlwerte (Optionen) je Feld.
 *
 * AP-36 Phase C: Jede Änderung ist eine **Operation** auf dem aktuellen Stand
 * der Liste (8.3 a), erkannt am Feldnamen, nicht an der Position in der Liste.
 * Zwei Administratoren, die gleichzeitig an verschiedenen Feldern arbeiten,
 * überschreiben einander nicht mehr. Anlegen bleibt ein Formular mit „Anlegen"
 * (Fall 2), Löschen eine Handlung mit Rückfrage.
 *
 * Menü-Neuordnung 2026-06, Stufe 2/Schritt 2: aus TimeConfigPage hierher
 * umgezogen, eingebunden in „Eigene Felder & Auswahllisten" (SelectionDataPage).
 *
 *  type="A" → Zeiterfassung (timeEntryCustomFieldsSchema, pro Buchung)
 *  type="B" → Lohnverrechnung (customFieldsSchema, einmal pro Tag)
 */

const UNIT_OPTIONS = [
  { value: '', label: 'Keine Einheit' },
  { value: 'km', label: 'km (Kilometer)' },
  { value: 'm', label: 'm (Meter)' },
  { value: 'h', label: 'h (Stunden)' },
  { value: 'min', label: 'min (Minuten)' },
  { value: 'stk', label: 'Stk. (Stück)' },
  { value: '%', label: '% (Prozent)' },
  { value: '€', label: '€ (Euro)' },
  { value: 'kg', label: 'kg (Kilogramm)' },
  { value: 'l', label: 'l (Liter)' },
  { value: 'other', label: 'Andere...' },
]

const AGG_OPTIONS: { value: 'none' | 'count' | 'sum' | 'avg'; label: string }[] = [
  { value: 'none', label: 'Keine' },
  { value: 'count', label: 'Anzahl' },
  { value: 'sum', label: 'Summe' },
  { value: 'avg', label: 'Mittelwert' },
]

interface NewFieldForm {
  name: string
  label: string
  type: 'text' | 'number' | 'time' | 'select' | 'checkbox'
  required: boolean
  unit: string
  unitCustom: string
  options: string // comma-separated for select type
  group: string
  aggregation: 'none' | 'count' | 'sum' | 'avg'
}

const DEFAULT_NEW_FIELD: NewFieldForm = {
  name: '',
  label: '',
  type: 'text',
  required: false,
  unit: '',
  unitCustom: '',
  options: '',
  group: '',
  aggregation: 'none',
}

const slugify = (text: string) =>
  text
    .toLowerCase()
    .replace(/[äöüß]/g, (c) => ({ ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' }[c] ?? c))
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 40)

function translateType(type: string): string {
  switch (type) {
    case 'text': return 'Text'
    case 'number': return 'Zahl'
    case 'time': return 'Uhrzeit'
    case 'select': return 'Auswahl'
    case 'checkbox': return 'Kontrollkästchen'
    default: return type
  }
}

interface Copy {
  title: string
  desc: string
  addTitle: string
  emptyTitle: string
  emptyHint: string
}

const COPY: Record<'A' | 'B', Copy> = {
  A: {
    title: 'Zusatzfelder Zeiterfassung',
    desc: 'Felder, die bei jeder einzelnen Zeitbuchung abgefragt werden (Stempeluhr & manuelle Erfassung). Beispiele: Auftrag, Tätigkeit, Bemerkung.',
    addTitle: 'Neues Zeiterfassungs-Zusatzfeld',
    emptyTitle: 'Noch keine Zeiterfassungs-Zusatzfelder konfiguriert.',
    emptyHint: 'Diese Felder erscheinen beim Einstempeln und in der manuellen Erfassung.',
  },
  B: {
    title: 'Zusatzfelder Lohnverrechnung',
    desc: 'Felder, die einmal pro Tag erfasst werden (im Tagesprotokoll der Mitarbeiter). Beispiele: gefahrene Kilometer, Schicht, Zulage.',
    addTitle: 'Neues Lohnverrechnungs-Zusatzfeld',
    emptyTitle: 'Noch keine Lohnverrechnungs-Zusatzfelder konfiguriert.',
    emptyHint: 'Diese Felder erscheinen im Tagesprotokoll der Mitarbeiter.',
  },
}

// Inline-Editor für die Auswahlwerte eines Select-Felds (Pills + Hinzufügen/Entfernen).
export function FieldOptionsEditor({
  options,
  onAdd,
  onRemove,
}: {
  options: string[]
  onAdd: (wert: string) => void
  onRemove: (wert: string) => void
}) {
  const [draft, setDraft] = useState('')
  const add = () => {
    const v = draft.trim()
    if (!v || options.includes(v)) {
      setDraft('')
      return
    }
    onAdd(v)
    setDraft('')
  }
  return (
    <div className="mt-2 space-y-2">
      <p className="text-xs font-medium text-foreground">Auswahlwerte (erscheinen im Dropdown)</p>
      <div className="flex flex-wrap gap-1.5">
        {options.length === 0 && (
          <span className="text-xs text-muted-foreground">Noch keine Werte — unten hinzufügen.</span>
        )}
        {options.map((opt) => (
          <span
            key={opt}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 text-xs text-foreground"
          >
            {opt}
            <button
              type="button"
              onClick={() => onRemove(opt)}
              className="text-muted-foreground hover:text-destructive"
              aria-label={`${opt} entfernen`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              add()
            }
          }}
          placeholder="Wert eingeben, Enter"
          className="w-full max-w-xs rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <Button
          type="button"
          onClick={add}
          variant="outline"
          size="sm"
          className="border-border text-foreground hover:bg-muted"
        >
          Hinzufügen
        </Button>
      </div>
    </div>
  )
}

/** Die Liste der Zusatzfelder aus einem Stand der Konfiguration. */
export const listeAus = (daten: Datensatz | undefined, schluessel: string): CustomFieldSchema[] =>
  Array.isArray(daten?.[schluessel]) ? (daten![schluessel] as CustomFieldSchema[]) : []

/** Operation: ein Feld (am Namen erkannt) ändern — auf dem jeweils aktuellen Stand. */
export const aendereFeld =
  (schluessel: string, name: string, aendern: (f: CustomFieldSchema) => CustomFieldSchema) => (aktuell: Datensatz) => ({
    [schluessel]: listeAus(aktuell, schluessel).map((f) => (f.name === name ? aendern(f) : f)),
  })

export default function CustomFieldsManager({ type }: { type: 'A' | 'B' }) {
  const { stelle, geladen, fehler } = useZeitKonfiguration()
  if (fehler) return <div className="py-6 text-sm text-destructive">{fehler}</div>
  if (!geladen) return <div className="py-6 text-sm text-muted-foreground">Wird geladen…</div>
  return <Zusatzfelder type={type} stelle={stelle as Speicherstelle<unknown>} />
}

function Zusatzfelder({ type, stelle }: { type: 'A' | 'B'; stelle: Speicherstelle<unknown> }) {
  // AP-16: Der Beispiel-Platzhalter nennt den Begriff des Mandanten.
  const terms = useTerminology()
  const copy = COPY[type]
  const schluessel = type === 'A' ? 'timeEntryCustomFieldsSchema' : 'customFieldsSchema'
  const daten = useStellenDaten(stelle)
  const liste = useAenderung(stelle, `${schluessel}:liste`)
  const fields = listeAus(daten, schluessel)
  const [showAdd, setShowAdd] = useState(false)
  const [newField, setNewField] = useState<NewFieldForm>(DEFAULT_NEW_FIELD)
  const [error, setError] = useState<string | null>(null)

  const handleLabelChange = (value: string) => {
    setNewField((prev) => ({
      ...prev,
      label: value,
      name: prev.name === '' || prev.name === slugify(prev.label) ? slugify(value) : prev.name,
    }))
  }

  const handleAdd = async () => {
    setError(null)
    const label = newField.label.trim()
    const name = newField.name.trim() || slugify(label)
    if (!label) {
      setError('Bitte eine Feldbeschriftung angeben.')
      return
    }
    if (!name) {
      setError('Bitte einen Feldnamen angeben.')
      return
    }
    if (fields.some((f) => f.name === name)) {
      setError(`Ein Feld mit dem Namen "${name}" existiert bereits.`)
      return
    }
    const effectiveUnit = newField.unit === 'other' ? newField.unitCustom.trim() : newField.unit
    const field: CustomFieldSchema = {
      name,
      label,
      type: newField.type,
      required: newField.required,
      ...(effectiveUnit && { unit: effectiveUnit }),
      ...(newField.group.trim() && { group: newField.group.trim() }),
      ...(newField.aggregation !== 'none' && { aggregation: newField.aggregation }),
    }
    if (newField.type === 'select' && newField.options) {
      field.options = newField.options
        .split(',')
        .map((o) => o.trim())
        .filter(Boolean)
    }
    const ergebnis = await liste.sende({
      felder: [schluessel],
      labels: { [schluessel]: copy.title },
      // Hat inzwischen jemand ein Feld gleichen Namens angelegt, bleibt dessen Feld stehen.
      operation: (aktuell) => {
        const jetzt = listeAus(aktuell, schluessel)
        return { [schluessel]: jetzt.some((f) => f.name === name) ? jetzt : [...jetzt, field] }
      },
    })
    if (ergebnis.art === 'gespeichert') {
      setNewField(DEFAULT_NEW_FIELD)
      setShowAdd(false)
    }
  }

  return (
    <div>
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h2 className={sectionTitle}>{copy.title}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {copy.desc} Bei Auswahl-Feldern pflegst du die Werte direkt unten am jeweiligen Feld.
          </p>
        </div>
        {!showAdd && (
          <Button
            onClick={() => {
              setShowAdd(true)
              setError(null)
            }}
            variant="outline"
            size="sm"
            className="shrink-0 border-border text-foreground hover:bg-muted"
          >
            + Feld hinzufügen
          </Button>
        )}
      </div>

      <FeldStatus status={liste.status} zeichen={false} />
      {error && (
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {showAdd && (
        <div className="mb-6 space-y-4 rounded-md border border-border/60 bg-muted/20 p-4">
          <h3 className={cardTitle}>{copy.addTitle}</h3>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <label htmlFor="cfLabel" className={fieldLabel}>
                Feldbeschriftung <span className="text-destructive">*</span>
              </label>
              <input
                id="cfLabel"
                type="text"
                value={newField.label}
                onChange={(e) => handleLabelChange(e.target.value)}
                placeholder="z.B. Auftragsnummer"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div className="space-y-1">
              <label htmlFor="cfName" className={fieldLabel}>
                Interner Feldname
              </label>
              <input
                id="cfName"
                type="text"
                value={newField.name}
                onChange={(e) => setNewField({ ...newField, name: e.target.value.replace(/[^a-z0-9_]/g, '') })}
                placeholder="z.B. auftragsnummer"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <p className="text-xs text-muted-foreground">Wird automatisch aus der Beschriftung abgeleitet</p>
            </div>

            <div className="space-y-1">
              <label htmlFor="cfType" className={fieldLabel}>
                Feldtyp
              </label>
              <select
                id="cfType"
                value={newField.type}
                onChange={(e) => { const ty = e.target.value as NewFieldForm['type']; setNewField({ ...newField, type: ty, aggregation: ty === 'number' || ty === 'time' ? 'sum' : ty === 'checkbox' ? 'count' : 'none' }) }}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="text">Text (Freitext)</option>
                <option value="number">Zahl</option>
                <option value="time">Uhrzeit (HH:MM)</option>
                <option value="select">Auswahl (Dropdown)</option>
                <option value="checkbox">Kontrollkästchen (Ja/Nein)</option>
              </select>
            </div>

            <div className="space-y-1">
              <label htmlFor="cfAgg" className={fieldLabel}>
                Auswertung in der Summenzeile
              </label>
              <select
                id="cfAgg"
                value={newField.aggregation}
                onChange={(e) => setNewField({ ...newField, aggregation: e.target.value as NewFieldForm['aggregation'] })}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {AGG_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
              </select>
            </div>

            {newField.type === 'number' && (
              <div className="space-y-1">
                <label htmlFor="cfUnit" className={fieldLabel}>
                  Einheit
                </label>
                <select
                  id="cfUnit"
                  value={newField.unit}
                  onChange={(e) => setNewField({ ...newField, unit: e.target.value })}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {UNIT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {newField.unit === 'other' && (
                  <input
                    type="text"
                    value={newField.unitCustom}
                    onChange={(e) => setNewField({ ...newField, unitCustom: e.target.value })}
                    placeholder="Eigene Einheit eingeben"
                    className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                )}
              </div>
            )}
          </div>

          {newField.type === 'select' && (
            <div className="space-y-1">
              <label htmlFor="cfOptions" className={fieldLabel}>
                Auswahlwerte <span className="text-muted-foreground text-xs">(kommagetrennt; später am Feld änderbar)</span>
              </label>
              <input
                id="cfOptions"
                type="text"
                value={newField.options}
                onChange={(e) => setNewField({ ...newField, options: e.target.value })}
                placeholder="z.B. Montage, Inbetriebnahme, Wartung"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          )}

          <div className="space-y-1">
            <label htmlFor="cfGroup" className={fieldLabel}>
              Gruppe <span className="text-muted-foreground text-xs">(optional – Felder mit gleichem Gruppennamen bilden einen Datensatz)</span>
            </label>
            <input
              id="cfGroup"
              type="text"
              value={newField.group}
              onChange={(e) => setNewField({ ...newField, group: e.target.value })}
              placeholder={`z.B. ${terms.project}, Kostenstelle`}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <label className="flex cursor-pointer items-center gap-3">
            <input
              type="checkbox"
              checked={newField.required}
              onChange={(e) => setNewField({ ...newField, required: e.target.checked })}
              className="h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-ring"
            />
            <span className="text-sm text-foreground">Pflichtfeld (Eingabe erforderlich)</span>
          </label>

          <div className="flex gap-2 pt-1">
            <Button
              onClick={handleAdd}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              size="sm"
            >
              Anlegen
            </Button>
            <Button
              onClick={() => {
                setShowAdd(false)
                setNewField(DEFAULT_NEW_FIELD)
                setError(null)
              }}
              variant="outline"
              size="sm"
              className="border-border text-foreground hover:bg-muted"
            >
              Abbrechen
            </Button>
          </div>
        </div>
      )}

      {fields.length > 0 ? (
        <div className="space-y-2">
          {fields.map((field) => (
            <FeldEintrag key={field.name} stelle={stelle} schluessel={schluessel} field={field} />
          ))}
        </div>
      ) : (
        !showAdd && (
          <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center">
            <p className="text-sm text-muted-foreground">{copy.emptyTitle}</p>
            <p className="mt-1 text-xs text-muted-foreground">{copy.emptyHint}</p>
          </div>
        )
      )}
    </div>
  )
}

/** Ein Zusatzfeld: Auswertung und Auswahlwerte speichern sofort, Löschen fragt nach. */
function FeldEintrag({ stelle, schluessel, field }: { stelle: Speicherstelle<unknown>; schluessel: string; field: CustomFieldSchema }) {
  const aenderung = useAenderung(stelle, `${schluessel}:${field.name}`)
  const label = { [schluessel]: field.label }
  const setzeAuswertung = (aggregation: CustomFieldSchema['aggregation']) =>
    void aenderung.sende({ felder: [schluessel], labels: label, wert: 'aggregation', operation: aendereFeld(schluessel, field.name, (f) => ({ ...f, aggregation })) })
  const wertHinzu = (wert: string) =>
    void aenderung.sende({
      felder: [schluessel],
      labels: label,
      operation: aendereFeld(schluessel, field.name, (f) => ({ ...f, options: (f.options ?? []).includes(wert) ? f.options : [...(f.options ?? []), wert] })),
    })
  const wertWeg = (wert: string) =>
    void aenderung.sende({
      felder: [schluessel],
      labels: label,
      operation: aendereFeld(schluessel, field.name, (f) => ({ ...f, options: (f.options ?? []).filter((o) => o !== wert) })),
    })
  const loeschen = async () => {
    const frage = `Zusatzfeld „${field.label}" löschen? Bereits erfasste Werte bleiben an den Buchungen stehen.`
    if (!(await bestaetige(frage, { ok: 'Löschen', gefaehrlich: true }))) return
    void aenderung.sende({
      felder: [schluessel],
      labels: label,
      operation: (aktuell) => ({ [schluessel]: listeAus(aktuell, schluessel).filter((f) => f.name !== field.name) }),
    })
  }
  return (
    <div className="flex items-start justify-between rounded-lg border border-border bg-background p-4">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium text-foreground">{field.label}</p>
          <span className="inline-block rounded border border-border bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
            {translateType(field.type)}
          </span>
          {field.required && (
            <span className="inline-block rounded bg-destructive/15 px-2 py-0.5 text-xs font-medium text-destructive">Pflichtfeld</span>
          )}
          <FeldStatusZeichen status={aenderung.status} />
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Interner Name: <code className="font-mono">{field.name}</code>
          {field.unit ? ` · Einheit: ${field.unit}` : ''}
          {field.group ? ` · Gruppe: ${field.group}` : ''}
        </p>
        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Auswertung:</span>
          <select
            aria-label={`Auswertung ${field.label}`}
            value={field.aggregation ?? 'none'}
            onChange={(e) => setzeAuswertung(e.target.value as CustomFieldSchema['aggregation'])}
            className="rounded-md border border-border bg-background px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {AGG_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        {field.type === 'select' && <FieldOptionsEditor options={field.options ?? []} onAdd={wertHinzu} onRemove={wertWeg} />}
        <FeldStatus status={aenderung.status} zeichen={false} />
      </div>
      <Button
        onClick={() => void loeschen()}
        variant="outline"
        size="sm"
        className="ml-4 shrink-0 border-border text-destructive hover:bg-destructive/10"
      >
        Löschen
      </Button>
    </div>
  )
}
